import React from 'react'
import { Logger } from 'wdc-cube'
import { styled } from '@mui/material/styles'

import Backdrop from '@mui/material/Backdrop'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import CircularProgress from '@mui/material/CircularProgress'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import LinearProgress from '@mui/material/LinearProgress'
import Switch from '@mui/material/Switch'
import TextField from '@mui/material/TextField'
import Tooltip from '@mui/material/Tooltip'

import ErrorOutlineIcon from '@mui/icons-material/ErrorOutlined'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'

import { DateFns } from 'src/utils/date-fns'
import { formatCPFOrCNPJ, formatNumber } from 'src/utils/formatter-utils'
import { alreadyExportedRowKey, ConfirmExportDataLimitScope } from 'src/main/Main.scopes'
import {
    buttonSx,
    chipSx,
    classToFComponent,
    FCClassContext,
    Field,
    FieldHelp,
    FieldLabel,
    inputSx,
    switchSx
} from 'src/utils/views'

const LOG = Logger.get('ConfirmExportDataLimit')

/* -------------------------------------------------------------------------- */
/* -------------------------------------------------------------------------- */

const ModalHead = styled('div')({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: 18,
    padding: '24px 26px 0'
})

const ModalTitle = styled('h2')({
    margin: '0 0 5px',
    fontSize: 20,
    lineHeight: 1.2,
    fontWeight: 700,
    color: 'var(--ink)'
})

const ModalSubtitle = styled('p')({
    margin: 0,
    color: 'var(--muted)',
    fontSize: 13
})

const Panel = styled('div')({
    marginTop: 18,
    padding: 16,
    border: '1px solid var(--border)',
    borderRadius: 11,
    backgroundColor: '#fbfcfb'
})

const PanelTitle = styled('h4')({
    margin: '0 0 12px',
    fontSize: 14,
    fontWeight: 700,
    color: 'var(--ink)'
})

const MetricGrid = styled('div')({
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: 8,
    '@media (max-width: 720px)': { gridTemplateColumns: 'minmax(0, 1fr)' }
})

const Metric = styled('div', { shouldForwardProp: (prop) => prop !== 'tone' })<{ tone?: 'neutral' | 'danger' }>(
    ({ tone }) => ({
        padding: '13px 14px',
        borderRadius: 9,
        border: `1px solid ${tone === 'danger' ? '#efb8b8' : 'var(--border)'}`,
        backgroundColor: tone === 'danger' ? 'var(--danger-bg)' : 'var(--surface)'
    })
)

const MetricValue = styled('div', { shouldForwardProp: (prop) => prop !== 'tone' })<{ tone?: 'neutral' | 'danger' }>(
    ({ tone }) => ({
        fontSize: 22,
        lineHeight: 1,
        fontWeight: 800,
        marginBottom: 7,
        color: tone === 'danger' ? 'var(--danger)' : 'var(--ink)'
    })
)

const MetricLabel = styled('div')({
    fontSize: 12,
    color: 'var(--muted)'
})

const ConsumptionBar = styled(LinearProgress)({
    height: 10,
    borderRadius: 999,
    backgroundColor: '#e6eae7',
    '& .MuiLinearProgress-bar': { borderRadius: 999 }
})

const Banner = styled('div', { shouldForwardProp: (prop) => prop !== 'tone' })<{ tone: 'blue' | 'warning' | 'danger' }>(
    ({ tone }) => {
        const palette = {
            blue: { color: '#1e4e91', borderColor: '#cddff8', background: 'var(--blue-bg)' },
            warning: { color: '#6d4605', borderColor: '#f3d6a3', background: 'var(--warning-bg)' },
            danger: { color: '#8f2020', borderColor: '#efb8b8', background: 'var(--danger-bg)' }
        }[tone]

        return {
            display: 'flex',
            alignItems: 'flex-start',
            gap: 11,
            marginTop: 14,
            padding: '12px 14px',
            borderRadius: 10,
            border: `1px solid ${palette.borderColor}`,
            backgroundColor: palette.background,
            color: palette.color,
            fontSize: 13,
            '& > .MuiSvgIcon-root': { fontSize: 20, flex: '0 0 auto', marginTop: 1 }
        }
    }
)

const BannerTitle = styled('div')({
    fontWeight: 700,
    marginBottom: 4
})

const BannerBody = styled('div')({
    minWidth: 0,
    lineHeight: 1.45
})

const AlreadyHeader = styled('div')({
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    marginBottom: 2
})

const AlreadySpacer = styled('div')({ flex: 1 })

const AlreadyToggleLabel = styled('span')({
    fontSize: 12,
    fontWeight: 700,
    whiteSpace: 'nowrap'
})

const AlreadyList = styled('div')({
    marginTop: 10,
    maxHeight: 180,
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
    gap: 6
})

const AlreadyItem = styled('div')({
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    minWidth: 0,
    padding: '7px 10px',
    borderRadius: 9,
    border: '1px solid var(--border)',
    backgroundColor: 'var(--surface)',
    color: 'var(--ink)'
})

const AlreadyItemInfo = styled('div')({
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minWidth: 0
})

const AlreadyItemLine = styled('div')({
    display: 'flex',
    alignItems: 'baseline',
    gap: 6,
    minWidth: 0
})

const AlreadyItemDoc = styled('span')({
    fontWeight: 750,
    fontSize: 13,
    whiteSpace: 'nowrap',
    flexShrink: 0
})

const AlreadyItemSep = styled('span')({
    color: 'var(--muted-2)',
    fontSize: 13,
    flexShrink: 0
})

const AlreadyItemName = styled('span')({
    flex: 1,
    minWidth: 0,
    fontWeight: 650,
    fontSize: 13,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
})

const AlreadyItemMeta = styled('span')({
    fontSize: 11.5,
    color: 'var(--muted)'
})

const ModalFooter = styled(DialogActions)({
    position: 'sticky',
    bottom: 0,
    justifyContent: 'flex-end',
    gap: 10,
    padding: '14px 26px',
    backgroundColor: 'rgba(255, 255, 255, .98)',
    borderTop: '1px solid var(--border)',
    '@media (max-width: 720px)': {
        flexDirection: 'column-reverse',
        '& .MuiButton-root': { width: '100%' }
    }
})

interface ConfirmExportDataLimitProps {
    scope: ConfirmExportDataLimitScope
}

class ConfirmExportDataLimitClass implements FCClassContext<ConfirmExportDataLimitProps> {
    scope!: ConfirmExportDataLimitScope

    readonly emitOnNoClicked = () => {
        this.scope.onYesNoClicked(false).catch(LOG.caught)
    }

    readonly emitOnYesClicked = () => {
        this.scope.onYesNoClicked(true).catch(LOG.caught)
    }

    readonly emitOnQttExportRecordsChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const newVal = e.target.value ? Number(e.target.value) : 0
        this.scope.onQttExportRecordsChange(newVal).catch(LOG.caught)
    }

    readonly emitOnToggleAlreadyExported = (key: string) => () => {
        this.scope.onToggleAlreadyExported(key).catch(LOG.caught)
    }

    readonly emitOnToggleAllAlreadyExported = () => {
        this.scope.onToggleAllAlreadyExported().catch(LOG.caught)
    }

    onSyncState({ scope }: ConfirmExportDataLimitProps) {
        this.scope = scope
    }

    private primaryLabel() {
        const { scope } = this
        if (scope.blocked) {
            return 'Exportação bloqueada'
        }
        if (!scope.availableExportQtdd) {
            return 'Sem dados para exportar'
        }
        return scope.exceeded ? 'Exportar mesmo assim' : 'Exportar dados'
    }

    render() {
        const { scope } = this
        const barColor = scope.exceeded ? 'var(--danger)' : scope.percent >= 90 ? 'var(--warning)' : 'var(--green-700)'

        return (
            <>
                <Backdrop
                    open={scope.isChecking}
                    sx={{
                        color: '#fff',
                        zIndex: (t) => t.zIndex.modal + 1,
                        flexDirection: 'column',
                        gap: 2,
                        backgroundColor: 'rgba(8, 11, 9, .38)',
                        backdropFilter: 'blur(1px)'
                    }}
                >
                    <CircularProgress color="inherit" />
                    <span>Verificando dados para exportação...</span>
                </Backdrop>

                <Dialog
                    open={scope.open}
                    onClose={this.emitOnNoClicked}
                    fullWidth
                    slotProps={{
                        backdrop: { sx: { backgroundColor: 'rgba(8, 11, 9, .38)' } },

                        paper: {
                            sx: {
                                width: 'min(760px, calc(100vw - 28px))',
                                maxWidth: 'none',
                                borderRadius: 'var(--radius)',
                                boxShadow: 'var(--shadow-2)',
                                backgroundImage: 'none',
                                color: 'var(--ink)',
                                fontSize: 14,
                                lineHeight: 1.45
                            }
                        }
                    }}
                >
                    <ModalHead>
                        <div>
                            <ModalTitle>Confirmar exportação de dados</ModalTitle>
                            <ModalSubtitle>Revise o resumo antes de continuar.</ModalSubtitle>
                        </div>
                        {scope.exceeded && <Chip size="small" label="Excede o limite" sx={chipSx('red')} />}
                    </ModalHead>

                    <DialogContent sx={{ padding: '18px 26px' }}>
                        <Field>
                            <FieldLabel htmlFor="exportLimit">Limitar registros a exportar</FieldLabel>
                            <TextField
                                id="exportLimit"
                                fullWidth
                                size="small"
                                type="number"
                                placeholder="Ex.: 500"
                                value={scope.qttExportRecords || ''}
                                onChange={this.emitOnQttExportRecordsChange}
                                sx={inputSx}
                            />
                            <FieldHelp>
                                Em branco, exporta todos os {formatNumber(scope.availableExportQtdd)} registros
                                disponíveis.
                            </FieldHelp>
                        </Field>

                        <Panel>
                            <PanelTitle>Resumo da exportação</PanelTitle>

                            <MetricGrid>
                                <Metric>
                                    <MetricValue>
                                        {scope.unlimitedDataExport
                                            ? 'Ilimitado'
                                            : formatNumber(scope.limitExportData || 0)}
                                    </MetricValue>
                                    <MetricLabel>Contratado</MetricLabel>
                                </Metric>

                                <Metric>
                                    <MetricValue>
                                        {formatNumber(scope.qttExportRecords || scope.availableExportQtdd)}
                                    </MetricValue>
                                    <MetricLabel>A exportar</MetricLabel>
                                </Metric>

                                {!scope.unlimitedDataExport && (
                                    <>
                                        <Metric>
                                            <MetricValue>
                                                {formatNumber(scope.currentMonthExportedData || 0)}
                                            </MetricValue>
                                            <MetricLabel>Consumo atual</MetricLabel>
                                        </Metric>

                                        <Metric tone={scope.exceeded ? 'danger' : 'neutral'}>
                                            <MetricValue tone={scope.exceeded ? 'danger' : 'neutral'}>
                                                {formatNumber(scope.newTotal)}
                                            </MetricValue>
                                            <MetricLabel>Novo consumo</MetricLabel>
                                        </Metric>
                                    </>
                                )}
                            </MetricGrid>

                            {!scope.unlimitedDataExport && (
                                <div style={{ marginTop: 16 }}>
                                    <FieldHelp style={{ marginBottom: 6 }}>
                                        {scope.percent.toFixed(0)}% do limite
                                    </FieldHelp>
                                    <ConsumptionBar
                                        variant="determinate"
                                        value={Math.min(scope.percent, 100)}
                                        sx={{ '& .MuiLinearProgress-bar': { backgroundColor: barColor } }}
                                    />
                                </div>
                            )}
                        </Panel>

                        {scope.alreadyExported && scope.alreadyExported.length > 0 && (
                            <Banner tone="blue">
                                <InfoOutlinedIcon />
                                <BannerBody>
                                    <AlreadyHeader>
                                        <BannerTitle style={{ marginBottom: 0 }}>
                                            {formatNumber(scope.alreadyExported.length)} documento(s) já exportado(s)
                                            anteriormente
                                        </BannerTitle>
                                        {scope.canExcludeAlreadyExported && (
                                            <>
                                                <AlreadySpacer />
                                                <AlreadyToggleLabel>Exportar todos</AlreadyToggleLabel>
                                                <Tooltip
                                                    title={
                                                        scope.allAlreadyExportedIncluded
                                                            ? 'Desmarcar todos'
                                                            : 'Marcar todos'
                                                    }
                                                >
                                                    <Switch
                                                        size="small"
                                                        sx={switchSx}
                                                        checked={scope.allAlreadyExportedIncluded}
                                                        onChange={this.emitOnToggleAllAlreadyExported}
                                                    />
                                                </Tooltip>
                                            </>
                                        )}
                                    </AlreadyHeader>

                                    <FieldHelp style={{ color: 'inherit', opacity: 0.85 }}>
                                        {scope.canExcludeAlreadyExported
                                            ? 'Já foram exportados por você ou pelo seu grupo e, por padrão, ficam de fora. Marque para incluí-los.'
                                            : 'Já foram exportados por você ou pelo seu grupo. A exportação não será bloqueada.'}
                                    </FieldHelp>

                                    <AlreadyList>
                                        {scope.alreadyExported.slice(0, 100).map((doc, idx) => (
                                            <AlreadyItem key={`${doc.docType}-${doc.docId}-${idx}`}>
                                                <AlreadyItemInfo>
                                                    <AlreadyItemLine>
                                                        <AlreadyItemDoc>{formatCPFOrCNPJ(doc.docId)}</AlreadyItemDoc>
                                                        {doc.docLabel && (
                                                            <>
                                                                <AlreadyItemSep>·</AlreadyItemSep>
                                                                <AlreadyItemName title={doc.docLabel}>
                                                                    {doc.docLabel}
                                                                </AlreadyItemName>
                                                            </>
                                                        )}
                                                    </AlreadyItemLine>
                                                    <AlreadyItemMeta>
                                                        {DateFns.formatDate(new Date(doc.dataHora), 'dd/MM/yyyy HH:mm')}{' '}
                                                        — {doc.userName}
                                                        {doc.groupName ? ` (${doc.groupName})` : ''}
                                                    </AlreadyItemMeta>
                                                </AlreadyItemInfo>

                                                {scope.canExcludeAlreadyExported && (
                                                    <Tooltip
                                                        title={doc.included ? 'Não exportar' : 'Exportar mesmo assim'}
                                                    >
                                                        <Switch
                                                            size="small"
                                                            sx={switchSx}
                                                            checked={doc.included}
                                                            onChange={this.emitOnToggleAlreadyExported(
                                                                alreadyExportedRowKey(doc)
                                                            )}
                                                        />
                                                    </Tooltip>
                                                )}
                                            </AlreadyItem>
                                        ))}
                                        {scope.alreadyExported.length > 100 && (
                                            <AlreadyItemMeta>
                                                ...e mais {formatNumber(scope.alreadyExported.length - 100)}{' '}
                                                documento(s).
                                            </AlreadyItemMeta>
                                        )}
                                    </AlreadyList>
                                </BannerBody>
                            </Banner>
                        )}

                        {scope.exceeded && !scope.blocked && (
                            <Banner tone="warning">
                                <ErrorOutlineIcon />
                                <BannerBody>
                                    <BannerTitle>Cobrança adicional</BannerTitle>A exportação excede o limite mensal
                                    contratado. Continuar resulta em cobrança adicional.
                                </BannerBody>
                            </Banner>
                        )}

                        {scope.blocked && (
                            <Banner tone="danger">
                                <ErrorOutlineIcon />
                                <BannerBody>
                                    <BannerTitle>Exportação bloqueada</BannerTitle>A quantidade excede a franquia mensal
                                    do grupo, e o administrador configurou o bloqueio de exportações acima desse limite.
                                    Fale com o administrador do grupo.
                                </BannerBody>
                            </Banner>
                        )}
                    </DialogContent>

                    <ModalFooter>
                        <Button sx={buttonSx()} onClick={this.emitOnNoClicked}>
                            Cancelar
                        </Button>

                        <Button
                            sx={buttonSx(scope.exceeded ? 'danger' : 'primary')}
                            onClick={this.emitOnYesClicked}
                            disabled={scope.isDownloading || !scope.availableExportQtdd || scope.blocked}
                        >
                            {scope.isDownloading ? <CircularProgress size={18} color="inherit" /> : this.primaryLabel()}
                        </Button>
                    </ModalFooter>
                </Dialog>
            </>
        )
    }
}

export default classToFComponent(ConfirmExportDataLimitClass)
